import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    MapPin, Mail, Phone, Users, Percent, Plus, MoreVertical,
    UserPlus, Edit3, RotateCcw, Tag, FileText, Receipt,
    MoveRight, Building2, Grid, List, Search, RefreshCcw
} from 'lucide-react';

import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Dropdown from '../components/ui/Dropdown';
import { useToast } from '../hooks/useToast';
import { fetchBranches, fetchBranch } from '../apiHelper';

/**
 * Branch Action Menu - Centralized for both Grid and List views
 */
const BranchActions = ({ branch, onAction }) => {
    const handleAction = (path, stateObj) => {
        onAction(path, { state: stateObj });
    }
    return (
        <div className="py-2 w-56">
            <div className="px-3 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Administration</div>
            <button onClick={() => handleAction('/branches/staff-to-branch', { branch_id: branch.store_id })} className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-gmail-blue transition-colors">
                <UserPlus size={16} /> Assign Staff
            </button>
            <button onClick={() => handleAction('/branches/edit', { store_id: branch.store_id })} className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-gmail-blue transition-colors">
                <Edit3 size={16} /> Edit Branch Info
            </button>
            <button onClick={() => handleAction('/branches/manage-discount', { storeId: branch.store_id })} className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-gmail-blue transition-colors">
                <Tag size={16} /> Manage Discounts
            </button>

            <div className="px-3 py-1 mt-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Operations</div>
            <button className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-gmail-green transition-colors">
                <FileText size={16} /> Raise Invoice
            </button>
            <button className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-gmail-green transition-colors">
                <Receipt size={16} /> Raise Receipt
            </button>
            <button className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors">
                <MoveRight size={16} /> Inventory Transfer
            </button>
            <button className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-gmail-red transition-colors">
                <RotateCcw size={16} /> Customer Returns
            </button>
        </div>
    )
};

const BranchManagement = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('grid');
    const [searchTerm, setSearchTerm] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchBranches();
            // const data2 = await fetchBranch(3)
            // console.log(data2)
            setBranches(data);
        } catch (err) {
            showToast("Failed to sync branch data", "error");
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => { loadData(); }, [loadData]);

    const filteredBranches = useMemo(() => {
        return branches.filter(b =>
            b.store_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            b.organization_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [branches, searchTerm]);

    return (
        <div className="flex flex-col h-[calc(100vh-110px)] bg-[#f8fafc] p-8 overflow-hidden">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
                <div>
                    <h1 className="text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                        <Building2 size={36} className="text-gmail-blue" />
                        Branches
                    </h1>
                    <p className="text-gray-500 mt-1 font-medium">Manage physical locations and regional operations</p>
                </div>

                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-800" size={18} />
                        <Input
                            placeholder="Search locations..."
                            className="pl-10 w-64 bg-white border-none shadow-sm focus:ring-2 ring-gmail-blue/20"
                            containerClassName="!mb-0"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex bg-white p-1 rounded-lg shadow-sm border border-gray-100">
                        <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-gmail-blue text-white shadow-md' : 'text-gray-400 hover:text-gray-600'}`}><Grid size={18} /></button>
                        <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-gmail-blue text-white shadow-md' : 'text-gray-400 hover:text-gray-600'}`}><List size={18} /></button>
                    </div>
                    <Button onClick={() => navigate('/branches/create')} size="sm" variant="primary" className="shadow-md shadow-blue-200 py-4 px-6 rounded-xl flex items-center gap-2 font-bold transition-transform hover:scale-105 active:scale-95">
                        <Plus size={18} strokeWidth={3} /> ADD NEW BRANCH
                    </Button>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto custom-scrollbar pb-10">
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gmail-blue">
                        <RefreshCcw className="animate-spin mb-4" size={40} />
                        <span className="font-bold tracking-widest text-xs uppercase">Syncing with HQ...</span>
                    </div>
                ) : filteredBranches.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4 text-gray-300">
                            <MapPin size={48} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-800">No Branches Found</h3>
                        <p className="text-gray-500 max-w-xs mx-auto">Either you haven't added a location or your search criteria returned nothing.</p>
                    </div>
                ) : (
                    <div className={viewMode === 'grid'
                        ? `grid gap-8 ${filteredBranches.length === 1 ? 'max-w-2xl mx-auto' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'}`
                        : "flex flex-col gap-4"
                    }>
                        {filteredBranches.map(branch => (
                            viewMode === 'grid' ? (
                                <BranchGridCard key={branch.store_id} branch={branch} navigate={navigate} />
                            ) : (
                                <BranchListRow key={branch.store_id} branch={branch} navigate={navigate} />
                            )
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * Modern Grid Card Design
 */
const BranchGridCard = ({ branch, navigate }) => (
    <Card className="group relative bg-white border-none shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] transition-all duration-500 rounded-2xl overflow-hidden p-0">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gmail-blue via-purple-400 to-gmail-green opacity-0 group-hover:opacity-100 transition-opacity" />

        <div className="p-6">
            <div className="flex justify-between items-start mb-6">
                <div className="p-3 bg-blue-50 text-gmail-blue rounded-xl group-hover:bg-gmail-blue group-hover:text-white transition-colors duration-300">
                    <Building2 size={24} />
                </div>
                <Dropdown trigger={<button className="p-2 hover:bg-gray-100 rounded-full transition-colors"><MoreVertical size={20} className="text-gray-400" /></button>} align="right">
                    <BranchActions branch={branch} onAction={navigate} />
                </Dropdown>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-1">{branch.store_name}</h3>
            <p className="text-xs font-bold text-gmail-blue tracking-tighter uppercase mb-4 opacity-70">{branch.organization_name}</p>

            <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3 text-sm text-gray-600 font-medium">
                    <MapPin size={16} className="text-gray-300" /> {branch.address1 || 'No Address Provided'}
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 font-medium">
                    <Phone size={16} className="text-gray-300" /> {branch.phone1 || 'No Contact'}
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 font-medium">
                    <Mail size={16} className="text-gray-300" /> {branch.email1}
                </div>
            </div>

            <div className="flex gap-2 pt-4 border-t border-gray-50">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-lg text-xs font-bold text-gray-500">
                    <Users size={14} className="text-gmail-blue" /> {branch.staff.length} Staff
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 rounded-lg text-xs font-bold text-gray-500">
                    <Percent size={14} className="text-gmail-green" /> {branch.taxes.length} Tax Rules
                </div>
                <div className="ml-auto text-xs font-black text-gray-300 uppercase">{branch.currency}</div>
            </div>
        </div>
    </Card>
);

/**
 * Modern List Row Design
 */
const BranchListRow = ({ branch, navigate }) => (
    <div className="group bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-6 hover:border-gmail-blue/30 transition-all">
        <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center text-gmail-blue group-hover:bg-gmail-blue group-hover:text-white transition-colors">
            <MapPin size={20} />
        </div>

        <div className="flex-1">
            <h4 className="font-bold text-gray-900 text-lg leading-none">{branch.store_name}</h4>
            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{branch.currency} Branch</span>
        </div>

        <div className="hidden lg:flex flex-col items-end px-6 border-r border-gray-100">
            <span className="text-xs font-bold text-gray-400 uppercase">Contact</span>
            <span className="text-sm font-medium text-gray-700">{branch.phone1}</span>
        </div>

        <div className="flex gap-4 px-6 border-r border-gray-100">
            <div className="flex flex-col items-center">
                <span className="text-[10px] font-bold text-gray-400 uppercase">Staff</span>
                <span className="font-bold text-gmail-blue">{branch.staff.length}</span>
            </div>
            <div className="flex flex-col items-center">
                <span className="text-[10px] font-bold text-gray-400 uppercase">Taxes</span>
                <span className="font-bold text-gmail-green">{branch.taxes.length}</span>
            </div>
        </div>

        <Dropdown trigger={<button className="p-2 hover:bg-gray-100 rounded-full transition-colors"><MoreVertical size={20} className="text-gray-400" /></button>} align="right">
            <BranchActions branch={branch} onAction={navigate} />
        </Dropdown>
    </div>
);

export default BranchManagement;